import { Row, Table } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAccountsTableColumns } from "./hooks/accountsTableColumns";
import { TablePagination } from "../../components/TablePagination";
import { useFetchData } from "./hooks/fetchData";
import { useFetchDataCount } from "./hooks/fetchDataCount";
import { Filters } from "./components/Filters";
import { useIsOther } from "./hooks/isOther";
import { NavigateFunction, useNavigate } from "react-router-dom";
import ROUTES from "../../configs/routesConfig";
import { useGetRole } from "../../lib/hooks/getRole";
import "./styles.scss";
import { PortalNotification } from "./components/PortalNotification";
import dayjs from "../../configs/dayjs";
import Cookies from "js-cookie";

const PORTAL_COUPONS_NOTIFICATION_COOKIE = "cuponsNotification";

export const Accounts = () => {
  const navigate: NavigateFunction = useNavigate();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, isAdmin] = useGetRole();

  const { isOther } = useIsOther();

  const {
    total,
    isLoading: isTotalLoading,
    request,
    fetchDataCount,
    setSearchParams,
  } = useFetchDataCount(isOther);

  const { data, isLoading, fetchData } = useFetchData(request);

  const reloadData = useCallback((): void => {
    fetchData();
    fetchDataCount();
  }, [fetchData, fetchDataCount]);

  const navigateToAccount = useCallback(
    (id: string) => {
      navigate(`${ROUTES.ACCOUNTS}/${id}`);
    },
    [navigate],
  );

  const onRow = useCallback(
    (record: any) => ({
      onClick: () => {
        const id = isAdmin ? record.id : record.accountNumber;

        navigate(`${ROUTES.ACCOUNTS}/${id}`);
      },
    }),
    [isAdmin, navigate],
  );

  const { columns, onSortChange } = useAccountsTableColumns(
    reloadData,
    navigateToAccount,
  );

  const onPageChange = (pageNumber: number, pageSize: number) => {
    const newRequest = { ...request, pageNumber, pageSize };

    setSearchParams(newRequest);
  };

  const onSorterChange = (_pagination: any, _filter: any, sorter: any) => {
    onSortChange(sorter, request);
  };

  const checkCouponsNotification: boolean = useMemo(() => {
    const notificationCookie = Cookies.get(PORTAL_COUPONS_NOTIFICATION_COOKIE);

    if (notificationCookie === "hide") {
      return false;
    }

    const now = dayjs();

    if (!isAdmin && now.year() < 2027) {
      return true;
    }

    return false;
  }, [isAdmin]);

  const [showCouponsNotification, setShowCouponsNotification] =
    useState<boolean>(checkCouponsNotification);

  useEffect(() => {
    setShowCouponsNotification(checkCouponsNotification);
  }, [checkCouponsNotification]);

  const onCloseNotification = useCallback(() => {
    setShowCouponsNotification(false);
  }, []);

  const containerClassName: string = useMemo(
    () =>
      `accounts table-page-content ${isAdmin ? "admin" : ""} ${showCouponsNotification ? "portal-with-not" : ""}`,
    [isAdmin, showCouponsNotification],
  );

  return (
    <Row className={containerClassName}>
      {showCouponsNotification && (
        <PortalNotification
          onClose={onCloseNotification}
          cookieName={PORTAL_COUPONS_NOTIFICATION_COOKIE}
        />
      )}
      <Filters request={request} isLoading={isLoading} isOther={isOther} />
      <Row className="d-block table__container w-100">
        <Table
          dataSource={data}
          columns={columns}
          loading={isLoading}
          pagination={false}
          className="overflow-y-auto clickable-rows-table"
          rowKey="id"
          onChange={onSorterChange}
          showSorterTooltip={false}
          onRow={onRow}
        />
        <TablePagination
          isTotalLoading={isTotalLoading}
          total={total}
          currentPage={request.pageNumber}
          pageSize={request.pageSize}
          onPageChange={onPageChange}
        />
      </Row>
    </Row>
  );
};
